/**
 * Um comando de configuração do rastreador, com as três formas de enviá-lo.
 *
 * Quem instala está com o aparelho numa mão e o celular na outra, agachado dentro de um carro —
 * não vai digitar `8040000 167.172.39.249 5013` sem errar, e o erro não avisa: o rastreador
 * simplesmente nunca fala. Daí as três saídas, em ordem de conveniência real:
 *
 * 1. **QR** — aponte o celular, o app de mensagens abre com número e texto prontos. É o caminho
 *    para quem está no computador com o celular em mãos, que é o caso normal.
 * 2. **Enviar** — o link `sms:`, para quem já está no próprio celular.
 * 3. **Copiar** — a saída de emergência, quando o resto falha.
 *
 * O QR é gerado NO NAVEGADOR, nunca por serviço externo: o texto do comando carrega a senha do
 * aparelho, e mandá-la a um gerador de terceiros seria entregá-la de bandeja.
 */
import { ArrowRight, Check, Copy, MessageSquare, QrCode } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type Comando = {
  chave: string;
  rotulo: string;
  descricao: string;
  /** O que passa a valer no aparelho depois deste SMS, com o valor concreto dentro. */
  efeito?: string;
  texto: string;
  resposta_esperada?: string;
  essencial?: boolean;
  pendencia: string | null;
};

/** `sms:` com o corpo pré-preenchido. O `?&` cobre iOS e Android com uma forma só. */
export function linkSms(destino: string, texto: string): string {
  return `sms:${destino}?&body=${encodeURIComponent(texto)}`;
}

function Qr({ conteudo }: { conteudo: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    QRCode.toCanvas(ref.current, conteudo, { width: 168, margin: 1 }).catch(() => setErro(true));
  }, [conteudo]);

  if (erro) {
    return (
      <p className="tipo-corpo-pequeno text-text-tertiary">
        Não consegui desenhar o QR. Use «Copiar» e cole no app de mensagens.
      </p>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-white p-3">
      {/* Fundo branco fixo: QR em tema escuro não é lido por leitor nenhum. */}
      <canvas ref={ref} />
      <span className="text-center text-[11px] leading-tight text-neutral-600">
        Aponte a câmera do celular
      </span>
    </div>
  );
}

export function ComandoSms({
  c,
  destino,
  ordem,
}: {
  c: Comando;
  destino: string | null;
  ordem?: number;
}) {
  const [copiado, setCopiado] = useState(false);
  const [mostrarQr, setMostrarQr] = useState(false);
  const bloqueado = !!c.pendencia || !destino;

  async function copiar() {
    try {
      await navigator.clipboard.writeText(c.texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1600);
    } catch {
      toast.error('O navegador não liberou a área de transferência.');
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        {ordem != null && (
          <span className="tipo-dado text-text-tertiary">{ordem}º</span>
        )}
        <span className="text-[15px] font-semibold text-text">{c.rotulo}</span>
      </div>
      <p className="tipo-corpo-pequeno text-text-secondary">{c.descricao}</p>

      {/* O que MUDA, separado do porquê. Quem está agachado dentro do carro precisa da frase
          curta com o número dentro, não do parágrafo que explica a motivação. */}
      {c.efeito && (
        <p className="tipo-corpo-pequeno flex items-start gap-2 rounded-md bg-surface-2 px-3 py-2 text-text">
          <ArrowRight className="mt-0.5 size-3.5 shrink-0 text-accent" aria-hidden />
          <span>
            <span className="tipo-rotulo">Depois deste SMS: </span>
            {c.efeito}
          </span>
        </p>
      )}

      <code
        className={cn(
          'tipo-dado select-all break-all rounded-md border border-border bg-surface-2 px-3 py-2 text-text',
          c.pendencia && 'border-warning/40 text-text-secondary',
        )}
      >
        {c.texto}
      </code>

      {c.pendencia ? (
        <p className="tipo-corpo-pequeno text-warning">{c.pendencia}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant={mostrarQr ? 'secundario' : 'primario'}
              disabled={bloqueado}
              onClick={() => setMostrarQr((v) => !v)}
            >
              <QrCode /> {mostrarQr ? 'Esconder QR' : 'Ler com o celular'}
            </Button>
            {destino && (
              <Button size="sm" variant="fantasma" asChild>
                <a href={linkSms(destino, c.texto)}>
                  <MessageSquare /> Abrir no celular
                </a>
              </Button>
            )}
            <Button size="sm" variant="fantasma" onClick={() => void copiar()} disabled={bloqueado}>
              {copiado ? <Check className="text-success" /> : <Copy />}
              {copiado ? 'Copiado' : 'Copiar'}
            </Button>
          </div>

          {mostrarQr && destino && (
            <div className="flex flex-wrap items-center gap-4 pt-1">
              <Qr conteudo={linkSms(destino, c.texto)} />
              <div className="flex max-w-xs flex-col gap-1">
                <p className="tipo-corpo-pequeno text-text-secondary">
                  A câmera abre o app de mensagens já com o número{' '}
                  <span className="tipo-dado">{destino}</span> e o texto preenchidos. Você só toca
                  em enviar.
                </p>
                <p className="tipo-corpo-pequeno text-text-tertiary">
                  O aparelho responde{' '}
                  <span className="tipo-dado">{c.resposta_esperada ?? 'SET OK'}</span> em alguns
                  segundos. Se não responder, ele está sem sinal de celular — não adianta mandar o
                  próximo.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
